import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiMenu } from 'react-icons/fi';
import dashboardStyles from '../AccueilDashboard.module.scss';
import widgetStyles from './Widget.module.scss';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import WidgetGraphe from './WidgetGraphe';
import WidgetPlan from './WidgetPlan';
import WidgetTableauDeBord from './WidgetTableauDeBord';

const STORAGE_KEY = 'dashboardSlots';
const DEFAULT_SLOTS = ['plan', 'graphe', 'dashboard'];

const WIDGET_CONFIG = {
  plan: { Component: WidgetPlan, labelKey: 'plan' },
  graphe: { Component: WidgetGraphe, labelKey: 'graphe' },
  dashboard: { Component: WidgetTableauDeBord, labelKey: 'tableau_de_bord' },
};

const sanitizeSlots = (slots) => {
  if (!Array.isArray(slots)) return null;
  const valid = slots.filter((id) => WIDGET_CONFIG[id]);
  if (valid.length === 0) return null;

  const unique = [];
  valid.forEach((id) => {
    if (!unique.includes(id)) unique.push(id);
  });

  DEFAULT_SLOTS.forEach((id) => {
    if (unique.length < 3 && !unique.includes(id)) {
      unique.push(id);
    }
  });

  return unique.length === 3 ? unique.slice(0, 3) : null;
};

const WidgetWidgetsLayout = ({
  dashboards = [],
  vuesFavorites = [],
  graphesFavoris = [],
  onDashboardClick,
  onVueClick,
  onGrapheClick,
}) => {
  const [slots, setSlots] = useState(DEFAULT_SLOTS);
  const [openMenuSlot, setOpenMenuSlot] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      const sanitized = sanitizeSlots(stored);
      if (sanitized) setSlots(sanitized);
    } catch {
      // ignore storage issues
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
  }, [slots]);

  useEffect(() => {
    if (openMenuSlot === null) return;
    const handleClick = (event) => {
      if (event.target.closest('[data-widget-menu-root]')) return;
      setOpenMenuSlot(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [openMenuSlot]);

  const areaConfig = useMemo(
    () => [
      { className: dashboardStyles.planArea, sizeVariant: 'large' },
      { className: dashboardStyles.grapheArea, sizeVariant: 'medium' },
      { className: dashboardStyles.tableauArea, sizeVariant: 'large' },
    ],
    []
  );

  const propsByWidget = useMemo(
    () => ({
      graphe: { graphesFavoris, onGrapheClick },
      plan: { vuesFavorites, onVueClick },
      dashboard: {
        dashboards,
        vuesFavorites,
        graphesFavoris,
        onDashboardClick,
        onVueClick,
        onGrapheClick,
        availableWidgets: ['dashboard'],
        initialWidget: 'dashboard',
      },
    }),
    [dashboards, vuesFavorites, graphesFavoris, onDashboardClick, onVueClick, onGrapheClick]
  );

  const handleSlotChange = useCallback((slotIndex, newWidget) => {
    setSlots((prev) => {
      if (prev[slotIndex] === newWidget) return prev;
      const next = [...prev];
      const existingIndex = prev.findIndex(
        (widgetId, idx) => widgetId === newWidget && idx !== slotIndex
      );
      if (existingIndex !== -1) {
        next[existingIndex] = prev[slotIndex];
      }
      next[slotIndex] = newWidget;
      return next;
    });
    setOpenMenuSlot(null);
  }, []);

  const widgetOptions = useMemo(
    () =>
      Object.entries(WIDGET_CONFIG).map(([id, cfg]) => ({
        value: id,
        label: checkMot(cfg.labelKey),
      })),
    []
  );

  const renderWidget = (widgetKey, slotIndex) => {
    const config = WIDGET_CONFIG[widgetKey];
    if (!config) return null;
    const Component = config.Component;
    const extraProps = propsByWidget[widgetKey] || {};
    const area = areaConfig[slotIndex] || {};

    const headerContent = (
      <div className={widgetStyles.dashboardWidgetHeader}>
        <div className={widgetStyles.dashboardHeaderControls}>
          <h3 className={widgetStyles.dashboardWidgetTitle}>
            {checkMot(config.labelKey)}
          </h3>
        </div>
        <div
          className={widgetStyles.dashboardWidgetMenuWrapper}
          data-widget-menu-root
        >
          <button
            type="button"
            className={widgetStyles.dashboardWidgetMenuButton}
            onClick={() =>
              setOpenMenuSlot((prev) =>
                prev === slotIndex ? null : slotIndex
              )
            }
            aria-haspopup="menu"
            aria-expanded={openMenuSlot === slotIndex}
          >
            <FiMenu size={18} />
          </button>
          {openMenuSlot === slotIndex && (
            <div className={widgetStyles.dashboardWidgetMenu} role="menu">
              {widgetOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={widgetStyles.dashboardWidgetMenuItem}
                  onClick={() => handleSlotChange(slotIndex, option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    const widgetProps = {
      ...extraProps,
      ...(widgetKey === 'dashboard' ? { sizeVariant: area.sizeVariant } : {}),
      headerContent,
      showHeader: false,
    };

    return <Component key={`${widgetKey}-${slotIndex}`} {...widgetProps} />;
  };

  return (
    <>
      {slots.map((widgetKey, slotIndex) => {
        const area = areaConfig[slotIndex] || {};
        return (
          <div
            key={`slot-${slotIndex}`}
            className={[dashboardStyles.area, area.className].join(' ')}
          >
            {renderWidget(widgetKey, slotIndex)}
          </div>
        );
      })}
    </>
  );
};

export default WidgetWidgetsLayout;
